import React, { useState, useEffect, useMemo } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { HelpCircle, X } from 'lucide-react';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import { Spinner } from '../ui/spinner';
import { Alert, AlertDescription } from '../ui/alert';
import { ToggleGroup, ToggleItem } from '../ui/toggle';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../ui/select';
import { flaggedChatsAPI } from '../../services/api';
import { flagTypeHex, flagTypeBadgeStyle } from './flagTypeColors';
import { agentLabel, BOT_STATUS_UNKNOWN_NOTICE } from './agentLabels';
import DateRangeFilter from './DateRangeFilter';
import FlagTypeFilter from './FlagTypeFilter';
import FlaggedThreadRow from './ThreadRow';

const ALL_AGENTS_ID = 'all';
const BUCKET_OPTIONS = [
  { value: 'day', label: 'Daily' },
  { value: 'week', label: 'Weekly' },
  { value: 'month', label: 'Monthly' },
];
// Capped rather than paginated — a single bucket's thread count for one flag type is
// expected to be small; if it isn't, narrowing the bucket (a smaller date range or a finer
// bucket size) is the fix, not a second page control bolted onto this panel.
const POINT_THREADS_PAGE_SIZE = 100;

// bucket_start comes from Redshift's date_trunc as an absolute UTC timestamp — formatting in
// the browser's local timezone would shift labels by a day (or a month, for the monthly
// bucket) for anyone east/west of UTC, same fix as PatternTrendChart.formatBucketLabel.
const formatBucketLabel = (iso, bucket) => {
  const date = new Date(iso);
  if (bucket === 'month') return date.toLocaleDateString('en-GB', { month: 'short', year: '2-digit', timeZone: 'UTC' });
  return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: '2-digit', timeZone: 'UTC' });
};

// Sorted largest-first (rather than series order) so the tooltip reads like a ranked
// breakdown of the hovered bucket instead of forcing a color-to-label lookup across a dozen
// similarly-hued lines. hoveredFlagType (set by hovering a line or its legend chip — see
// AgentAnalysisTab) bolds that one row, so which line you're actually pointing at doesn't
// depend on distinguishing similar hues by eye.
const AgentTrendTooltip = ({ active, payload, label, hoveredFlagType }) => {
  if (!active || !payload?.length) return null;
  const nonZero = [...payload].filter((p) => p.value > 0).sort((a, b) => b.value - a.value);
  const total = nonZero.reduce((sum, p) => sum + p.value, 0);
  return (
    <div className="rounded border border-white/10 bg-surface-1 px-2.5 py-2 text-[11px] space-y-1 min-w-[160px]">
      <div className="flex items-center justify-between gap-3">
        <span className="font-bold text-ink">{label}</span>
        <span className="text-ink-mute" title="Flags raised in this period — a conversation with several flags counts once per flag">{total} flag{total === 1 ? '' : 's'}</span>
      </div>
      {nonZero.length === 0 ? (
        <p className="text-ink-dim">No flags</p>
      ) : nonZero.map((p) => {
        const isHovered = p.dataKey === hoveredFlagType;
        return (
          <p key={p.dataKey} className={`flex items-center gap-1.5 ${isHovered ? 'bg-surface-2 -mx-1 px-1 rounded' : ''}`}>
            <span className="inline-block w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: p.color }} />
            <span className={`flex-1 truncate ${isHovered ? 'text-ink font-bold' : 'text-ink-dim'}`}>{p.dataKey}</span>
            <span className={isHovered ? 'text-cta font-bold' : 'text-ink font-bold'}>{p.value}</span>
          </p>
        );
      })}
    </div>
  );
};

const AgentSelect = ({ agents, value, onChange, botStatusKnown }) => (
  <div className="flex items-center gap-1.5">
    <span className="font-mono text-[10px] uppercase tracking-wider text-ink-mute">Agent</span>
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger className="h-8 w-56 text-xs"><SelectValue /></SelectTrigger>
      <SelectContent>
        <SelectItem value={ALL_AGENTS_ID}>All agents</SelectItem>
        {agents.map((a) => (
          <SelectItem key={a.agent_id} value={a.agent_id}>{agentLabel(a.agent_id)}</SelectItem>
        ))}
      </SelectContent>
    </Select>
    {!botStatusKnown && (
      <span className="shrink-0 text-ink-mute" title={BOT_STATUS_UNKNOWN_NOTICE} aria-label={BOT_STATUS_UNKNOWN_NOTICE}>
        <HelpCircle className="h-3.5 w-3.5" aria-hidden="true" />
      </span>
    )}
  </div>
);

// Flag volume over time for one agent, every bot, or every agent combined — a tab within the
// Flagged Chats report rather than its own nav entry, since it's just a different lens on the
// same flagged_threads data.
function AgentAnalysisTab() {
  const [dateBounds, setDateBounds] = useState(null);
  const [range, setRange] = useState(null);
  const [agents, setAgents] = useState([]);
  // Defaults to true so the caveat only ever appears once the API has actually reported that
  // bot classification is missing — not during the initial load, when nothing is known yet.
  const [botStatusKnown, setBotStatusKnown] = useState(true);
  const [flagTypes, setFlagTypes] = useState([]);
  const [includedFlagTypes, setIncludedFlagTypes] = useState([]);
  const [loadError, setLoadError] = useState('');
  const [selectedAgent, setSelectedAgent] = useState(ALL_AGENTS_ID);
  const [bucket, setBucket] = useState('day');
  const [trend, setTrend] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [hoveredFlagType, setHoveredFlagType] = useState(null);
  const [selectedPoint, setSelectedPoint] = useState(null);
  const [pointThreads, setPointThreads] = useState({ loading: false, error: '', threads: [], total: 0 });
  const [expandedPointThreadId, setExpandedPointThreadId] = useState(null);

  // Agent/flag type option lists + the default "all time" range only need loading once.
  useEffect(() => {
    (async () => {
      try {
        const [boundsResp, agentsResp, flagTypesResp] = await Promise.all([
          flaggedChatsAPI.getDateBounds(),
          flaggedChatsAPI.getAgents(),
          flaggedChatsAPI.getFlagTypes(),
        ]);
        const bounds = boundsResp.data;
        setDateBounds(bounds);
        setRange(
          bounds.min_start_at && bounds.max_start_at
            ? { from: bounds.min_start_at, to: bounds.max_start_at }
            : { from: null, to: null },
        );
        setAgents(agentsResp.data.agents || []);
        setBotStatusKnown(agentsResp.data.bot_status_known !== false);
        setFlagTypes(flagTypesResp.data.flag_types || []);
      } catch {
        setLoadError('Failed to load filters.');
        setRange({ from: null, to: null });
      }
    })();
  }, []);

  useEffect(() => {
    if (!range) return undefined;
    let cancelled = false;
    setSelectedPoint(null); // stale index/bucket boundaries once the trend itself reloads
    (async () => {
      try {
        setLoading(true);
        setError('');
        const agent = selectedAgent === ALL_AGENTS_ID ? '' : selectedAgent;
        const resp = await flaggedChatsAPI.getAgentTrend(agent, bucket, range.from, range.to);
        if (cancelled) return;
        setTrend(resp.data);
      } catch {
        if (!cancelled) setError('Failed to load agent trend.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [selectedAgent, bucket, range]);

  // selectedPoint pins one (flag_type, bucket) — the bucket's end is the next bucket's start
  // (minus a second, so the boundary itself isn't double-counted under both buckets), or the
  // chart's own end-of-range for the last bucket, which may run past its nominal width.
  useEffect(() => {
    if (!selectedPoint) return undefined;
    let cancelled = false;
    (async () => {
      try {
        setPointThreads({ loading: true, error: '', threads: [], total: 0 });
        const agent = selectedAgent === ALL_AGENTS_ID ? '' : selectedAgent;
        const resp = await flaggedChatsAPI.getThreads({
          from: selectedPoint.from, to: selectedPoint.to, flagTypes: [selectedPoint.flagType], agent,
          pageSize: POINT_THREADS_PAGE_SIZE,
        });
        if (cancelled) return;
        setPointThreads({ loading: false, error: '', threads: resp.data.threads || [], total: resp.data.total || 0 });
      } catch {
        if (!cancelled) setPointThreads({ loading: false, error: 'Failed to load conversations.', threads: [], total: 0 });
      }
    })();
    return () => { cancelled = true; };
  }, [selectedPoint, selectedAgent]);

  const handleFlagTypeToggle = (flagType) => {
    setSelectedPoint(null); // the clicked line may no longer be visible
    setIncludedFlagTypes((prev) => (
      prev.includes(flagType) ? prev.filter((ft) => ft !== flagType) : [...prev, flagType]
    ));
  };

  const handleFlagTypeClear = () => {
    setSelectedPoint(null);
    setIncludedFlagTypes([]);
  };

  // Clicking a legend chip narrows the flag-type filter above the chart to just that one
  // value — same effect as clearing the filter and picking only this flag type there,
  // just reachable without leaving the chart.
  const handleLegendClick = (flagType) => {
    setSelectedPoint(null);
    setIncludedFlagTypes([flagType]);
  };

  // Empty selection means no filter (show every flag type the agent actually triggered) —
  // same "empty = all" convention as the Flagged Threads tab's filters.
  const visibleSeries = useMemo(() => {
    if (!trend?.series) return [];
    return includedFlagTypes.length === 0
      ? trend.series
      : trend.series.filter((s) => includedFlagTypes.includes(s.flag_type));
  }, [trend, includedFlagTypes]);

  const chartData = useMemo(() => {
    if (!trend?.buckets?.length) return [];
    return trend.buckets.map((bucketStart, i) => {
      const row = { label: formatBucketLabel(bucketStart, bucket) };
      visibleSeries.forEach((s) => { row[s.flag_type] = s.counts[i]; });
      return row;
    });
  }, [trend, bucket, visibleSeries]);

  const handlePointClick = (flagType, index) => {
    if (!trend?.buckets?.length) return;
    const bucketStart = trend.buckets[index];
    const nextBucketStart = trend.buckets[index + 1];
    const bucketEnd = nextBucketStart
      ? new Date(new Date(nextBucketStart).getTime() - 1000).toISOString()
      : range.to;
    setSelectedPoint({ flagType, from: bucketStart, to: bucketEnd, label: chartData[index]?.label });
    setExpandedPointThreadId(null);
  };

  const togglePointThread = (threadId) => {
    setExpandedPointThreadId((prev) => (prev === threadId ? null : threadId));
  };

  // Shared across every reviewer, same as the Flagged Threads tab — updates the locally held
  // point-threads list directly rather than refetching, since there's no checked-status
  // filter here to potentially drop the thread out of view.
  const handleTogglePointChecked = async (threadId, checked, startAt) => {
    let checkedBy;
    try {
      const resp = await flaggedChatsAPI.setThreadChecked(threadId, checked, startAt);
      checkedBy = resp.data.checked_by;
    } catch {
      setPointThreads((prev) => ({ ...prev, error: 'Failed to update checked status.' }));
      return;
    }
    setPointThreads((prev) => ({
      ...prev,
      threads: prev.threads.map((t) => (
        t.thread_id === threadId ? { ...t, checked, checked_by: checked ? checkedBy : null } : t
      )),
    }));
  };

  const hasData = visibleSeries.length > 0;

  return (
    <div>
      {loadError && <Alert variant="error" className="mb-4"><AlertDescription>{loadError}</AlertDescription></Alert>}

      <div className="mb-4 flex items-center justify-between flex-wrap gap-3">
        <AgentSelect agents={agents} value={selectedAgent} onChange={setSelectedAgent} botStatusKnown={botStatusKnown} />
        <div className="flex items-center gap-2">
          <span className="font-mono text-[10px] uppercase tracking-wider text-ink-mute">Bucket</span>
          <ToggleGroup value={bucket} onChange={setBucket}>
            {BUCKET_OPTIONS.map((o) => <ToggleItem key={o.value} value={o.value}>{o.label}</ToggleItem>)}
          </ToggleGroup>
        </div>
      </div>

      <div className="mb-4">
        <DateRangeFilter range={range} bounds={dateBounds} onChange={setRange} />
      </div>

      <div className="mb-4">
        <FlagTypeFilter
          flagTypes={flagTypes} selected={includedFlagTypes} label="Flag types (leave empty to show all)"
          onToggle={handleFlagTypeToggle} onClear={handleFlagTypeClear}
        />
      </div>

      <Card>
        <CardContent className="p-4">
          {error && <Alert variant="error" className="mb-4"><AlertDescription>{error}</AlertDescription></Alert>}
          {loading ? (
            <div className="flex justify-center py-16"><Spinner /></div>
          ) : !hasData ? (
            <div className="flex flex-col items-center justify-center py-16 gap-2">
              <p className="text-sm font-bold text-ink">No flags</p>
              <p className="text-xs text-ink-mute">Nothing flagged for this agent in the selected range.</p>
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={360}>
              <LineChart data={chartData} margin={{ top: 8, right: 16, bottom: 0, left: 0 }}>
                <CartesianGrid stroke="#262c3d" vertical={false} />
                <XAxis
                  dataKey="label" tick={{ fontSize: 10, fill: '#9aa0ad' }} tickLine={false}
                  axisLine={{ stroke: '#262c3d' }}
                />
                <YAxis
                  allowDecimals={false} tick={{ fontSize: 10, fill: '#9aa0ad' }} tickLine={false}
                  axisLine={{ stroke: '#262c3d' }} width={32}
                />
                {/* A visible dashed crosshair at the hovered bucket — with a dozen similarly
                    hued lines, the cursor alone made it hard to tell which x-position (and
                    which line) was actually under the mouse. */}
                <Tooltip
                  content={(props) => <AgentTrendTooltip {...props} hoveredFlagType={hoveredFlagType} />}
                  cursor={{ stroke: '#9aa0ad', strokeWidth: 1, strokeDasharray: '4 4' }}
                />
                {visibleSeries.map((s) => {
                  const isHovered = hoveredFlagType === s.flag_type;
                  const isDimmed = hoveredFlagType && !isHovered;
                  // Custom dot/activeDot renderers (rather than the plain boolean/style-object
                  // shorthand) so each point is clickable — picks the specific line's threads
                  // for that bucket, not the whole bucket across every flag type.
                  const renderDot = (dotProps) => {
                    if (chartData.length > 40) return null;
                    const { cx, cy, index, key } = dotProps;
                    return (
                      <circle
                        key={key} cx={cx} cy={cy} r={3} fill={flagTypeHex(s.flag_type)}
                        stroke="#0a0b10" strokeWidth={1} style={{ cursor: 'pointer' }}
                        onClick={() => handlePointClick(s.flag_type, index)}
                      />
                    );
                  };
                  const renderActiveDot = (dotProps) => {
                    const { cx, cy, index, key } = dotProps;
                    return (
                      <circle
                        key={key} cx={cx} cy={cy} r={5} fill={flagTypeHex(s.flag_type)}
                        stroke="#0a0b10" strokeWidth={2} style={{ cursor: 'pointer' }}
                        onClick={() => handlePointClick(s.flag_type, index)}
                      />
                    );
                  };
                  return (
                    <Line
                      key={s.flag_type}
                      type="monotone" dataKey={s.flag_type} name={s.flag_type} stroke={flagTypeHex(s.flag_type)}
                      strokeWidth={isHovered ? 3 : 2} strokeOpacity={isDimmed ? 0.25 : 1}
                      dot={renderDot}
                      activeDot={renderActiveDot}
                      isAnimationActive={false}
                      onMouseEnter={() => setHoveredFlagType(s.flag_type)}
                      onMouseLeave={() => setHoveredFlagType(null)}
                    />
                  );
                })}
              </LineChart>
            </ResponsiveContainer>
          )}
          {hasData && (
            <div className="flex flex-wrap gap-x-4 gap-y-1.5 mt-3 pt-3 border-t border-white/10">
              {/* Hovering a legend chip highlights that line on the chart too — a bigger,
                  easier target than the line itself for picking out one flag type. Clicking
                  it narrows the flag-type filter above to just that one value. */}
              {visibleSeries.map((s) => (
                <button
                  type="button"
                  key={s.flag_type}
                  onClick={() => handleLegendClick(s.flag_type)}
                  onMouseEnter={() => setHoveredFlagType(s.flag_type)}
                  onMouseLeave={() => setHoveredFlagType(null)}
                  className={`flex items-center gap-1.5 text-[11px] cursor-pointer transition-colors ${
                    hoveredFlagType === s.flag_type ? 'text-ink font-bold' : 'text-ink-dim'
                  }`}
                >
                  <span className="inline-block w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: flagTypeHex(s.flag_type) }} />
                  {s.flag_type}
                </button>
              ))}
            </div>
          )}
          {selectedPoint && (
            <div className="mt-3 pt-3 border-t border-white/10">
              <div className="flex items-center justify-between gap-2 mb-3">
                <div className="flex items-center gap-2 text-xs min-w-0">
                  <Badge style={flagTypeBadgeStyle(selectedPoint.flagType)}>{selectedPoint.flagType}</Badge>
                  <span className="text-ink-mute truncate">{selectedPoint.label}</span>
                  <span className="text-ink-mute">·</span>
                  <span className="text-ink font-bold whitespace-nowrap">
                    {pointThreads.total} conversation{pointThreads.total === 1 ? '' : 's'}
                  </span>
                </div>
                <button
                  type="button" onClick={() => setSelectedPoint(null)}
                  className="text-ink-mute hover:text-ink shrink-0" title="Close"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              </div>
              {pointThreads.loading ? (
                <div className="flex justify-center py-6"><Spinner /></div>
              ) : pointThreads.error ? (
                <Alert variant="error"><AlertDescription>{pointThreads.error}</AlertDescription></Alert>
              ) : pointThreads.threads.length === 0 ? (
                <p className="text-xs text-ink-mute py-2">No conversations found for this point.</p>
              ) : (
                <div className="divide-y divide-white/5 max-h-[32rem] overflow-y-auto custom-scrollbar pr-1">
                  {pointThreads.threads.map((t) => (
                    <FlaggedThreadRow
                      key={t.thread_id}
                      thread={t}
                      isExpanded={expandedPointThreadId === t.thread_id}
                      onToggle={togglePointThread}
                      onToggleChecked={handleTogglePointChecked}
                    />
                  ))}
                </div>
              )}
              {pointThreads.total > pointThreads.threads.length && (
                <p className="text-[11px] text-ink-mute mt-2">
                  Showing the first {pointThreads.threads.length} of {pointThreads.total} — narrow the date range
                  or bucket size to see the rest.
                </p>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

export default AgentAnalysisTab;
